import unfiLogo from "/img/integration/unfi.png"
import { useLocation } from "wouter-preact"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { LoadingSpinner } from "@/app/loading"
import {
  getIntegrations,
  Integration,
  IntegrationWithJobInfo,
  UnfiConfig,
  updateIntegration,
} from "@/api/integration"
import { ConnectLogo } from "@/setup/connect/connect-logo"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx"
import { toast } from "@/components/ui/use-toast"
import { PasswordViewCopyButtons } from "@/settings/integrations/password-view-copy-buttons"
import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import { parseISO, format } from "date-fns"
import { EmailSelector } from "@/setup/config/email-config-page"

interface UnfiConfigFormState {
  unfi_username: string
  unfi_password: string
  vendor_name: string
  remit_number: string
  discount_term: string
  dispute_from_full_name: string
  use_spoilage_allowance: boolean
  ssa_has_agreement: boolean
  ssa_signed_date: string
  ssa_proactive_discount: boolean
  ssa_invoice_start_date: string
  ssa_discount_percentage: string
  clearvue_has_agreement: boolean
  clearvue_signed_date: string
  clearvue_proactive_discount: boolean
  clearvue_invoice_start_date: string
  clearvue_discount_percentage: string
  delivery_method: string
}

export interface UnfiConfigFormData {
  vendor_name: string | null
  remit_number: string | null
  discount_term: string | null
  dispute_from_full_name: string | null
  use_spoilage_allowance: boolean
}

interface UnfiConfigFormProps {
  integration: Integration
  emailAccounts: IntegrationWithJobInfo[]
  onSuccess: (int: Integration) => void
}

function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  try {
    return format(parseISO(dateStr), "yyyy-MM-dd")
  } catch {
    return ""
  }
}

export function UnfiConfigForm({ integration, emailAccounts, onSuccess }: UnfiConfigFormProps) {
  const [loading, setLoading] = useState(false)
  const data = integration.data as unknown as UnfiConfig
  const [selectedEmail, setSelectedEmail] = useState(data.email_address || "")

  const form = useFormState<UnfiConfigFormState>({
    unfi_username: integration.username ?? "",
    unfi_password: "",
    vendor_name: data.vendor_name ?? "",
    remit_number: data.remit_number?.toString() ?? "",
    discount_term: data.discount_term?.toString() ?? "",
    dispute_from_full_name: data.dispute_from_full_name ?? "",
    use_spoilage_allowance: data.use_spoilage_allowance ?? false,
    ssa_has_agreement: data.ssa_has_agreement ?? false,
    ssa_signed_date: formatDateForInput(data.ssa_signed_date),
    ssa_proactive_discount: data.ssa_proactive_discount ?? false,
    ssa_invoice_start_date: formatDateForInput(data.ssa_invoice_start_date),
    ssa_discount_percentage: data.ssa_discount_percentage?.toString() ?? "",
    clearvue_has_agreement: data.clearvue_has_agreement ?? false,
    clearvue_signed_date: formatDateForInput(data.clearvue_signed_date),
    clearvue_proactive_discount: data.clearvue_proactive_discount ?? false,
    clearvue_invoice_start_date: formatDateForInput(data.clearvue_invoice_start_date),
    clearvue_discount_percentage: data.clearvue_discount_percentage?.toString() ?? "",
    delivery_method: data.delivery_method ?? "",
  })

  async function onSubmit() {
    setLoading(true)
    form.set_form_errors([])

    const discountTerm = parseInt(form.discount_term.value, 10)
    const ssaDiscountPercentage = parseFloat(form.ssa_discount_percentage.value)
    const clearvueDiscountPercentage = parseFloat(form.clearvue_discount_percentage.value)

    function parseDateInput(dateStr: string): string | null {
      if (!dateStr) return null
      try {
        const date = new Date(dateStr + "T00:00:00")
        return date.toISOString()
      } catch {
        return null
      }
    }

    const config: UnfiConfig = {
      email_address: selectedEmail,
      vendor_name: form.vendor_name.value || null,
      remit_number: form.remit_number.value.trim() || null,
      discount_term: isNaN(discountTerm) ? null : discountTerm,
      dispute_from_full_name: form.dispute_from_full_name.value || null,
      use_spoilage_allowance: form.use_spoilage_allowance.value,
      ssa_has_agreement: form.ssa_has_agreement.value,
      ssa_signed_date: parseDateInput(form.ssa_signed_date.value),
      ssa_proactive_discount: form.ssa_proactive_discount.value,
      ssa_invoice_start_date: parseDateInput(form.ssa_invoice_start_date.value),
      ssa_discount_percentage: isNaN(ssaDiscountPercentage) ? null : ssaDiscountPercentage,
      clearvue_has_agreement: form.clearvue_has_agreement.value,
      clearvue_signed_date: parseDateInput(form.clearvue_signed_date.value),
      clearvue_proactive_discount: form.clearvue_proactive_discount.value,
      clearvue_invoice_start_date: parseDateInput(form.clearvue_invoice_start_date.value),
      clearvue_discount_percentage: isNaN(clearvueDiscountPercentage) ? null : clearvueDiscountPercentage,
      delivery_method: form.delivery_method.value || null,
    }
    const updates: Record<string, unknown> = {
      data: config,
      username: form.unfi_username.value,
    }
    // Only include credential if password was entered
    if (form.unfi_password.value) {
      updates.credential = { password: form.unfi_password.value }
    }
    const res = await updateIntegration(integration.id, updates)
    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to save configuration. Please try again."])
      return
    }
    toast({
      title: "Settings saved",
      description: "Your UNFI configuration has been updated.",
    })
    onSuccess(res.value.data)
  }

  return (
    <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
      <EmailSelector
        emailAccounts={emailAccounts}
        selectedEmail={selectedEmail}
        onSelect={setSelectedEmail}
        description="Choose which email account to use for sending disputes."
      />

      <div class="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 class="font-medium text-gray-900">UNFI Dispute Portal Credentials (optional)</h3>
        <Input
          label="Username"
          type="text"
          name="unfi_username"
          placeholder="Your UNFI portal username"
          bind={form.unfi_username}
        />
        <Input
          label="Password"
          type="password"
          name="unfi_password"
          placeholder={integration.username ? "Enter new password to change" : "Your UNFI portal password"}
          bind={form.unfi_password}
        />
        <PasswordViewCopyButtons integrationId={integration.id} />
      </div>

      <Input
        label="Vendor Name"
        type="text"
        name="vendor_name"
        placeholder="Your Company Inc"
        bind={form.vendor_name}
      />

      <Input
        label="Remit Number"
        type="text"
        name="remit_number"
        placeholder="Vendor number from UNFI checks (UNFI Canada uses letters)"
        bind={form.remit_number}
      />

      <Input label="Discount Term (days)" type="number" name="discount_term" bind={form.discount_term} />

      <Input
        label="Dispute From Name"
        type="text"
        name="dispute_from_full_name"
        placeholder="Name to use when sending disputes"
        bind={form.dispute_from_full_name}
      />

      <div class="flex items-center space-x-3">
        <Checkbox
          id="use_spoilage_allowance"
          checked={form.use_spoilage_allowance.value}
          onCheckedChange={checked => {
            form.set_form_data({ use_spoilage_allowance: checked === true })
          }}
        />
        <Label htmlFor="use_spoilage_allowance" class="text-sm font-medium text-gray-700">
          Use spoilage allowance
        </Label>
      </div>

      <div class="space-y-4">
        <div class="flex items-center space-x-3">
          <Checkbox
            id="ssa_has_agreement"
            checked={form.ssa_has_agreement.value}
            onCheckedChange={checked => {
              form.set_form_data({ ssa_has_agreement: checked === true })
            }}
          />
          <Label htmlFor="ssa_has_agreement" class="text-sm font-medium text-gray-700">
            Has SSA agreement
          </Label>
        </div>
        {form.ssa_has_agreement.value && (
          <div class="ml-7 space-y-4">
            <Input label="SSA Signed Date" type="date" name="ssa_signed_date" bind={form.ssa_signed_date} />
            <div class="flex items-center space-x-3">
              <Checkbox
                id="ssa_proactive_discount"
                checked={form.ssa_proactive_discount.value}
                onCheckedChange={checked => {
                  form.set_form_data({ ssa_proactive_discount: checked === true })
                }}
              />
              <Label htmlFor="ssa_proactive_discount" class="text-sm font-medium text-gray-700">
                Proactively give SSA discount on invoice
              </Label>
            </div>
            {form.ssa_proactive_discount.value && (
              <div class="ml-7">
                <Input
                  label="SSA Invoice Start Date"
                  type="date"
                  name="ssa_invoice_start_date"
                  bind={form.ssa_invoice_start_date}
                />
              </div>
            )}
            <Input
              label="SSA Discount Percentage"
              type="number"
              name="ssa_discount_percentage"
              placeholder="0.00"
              step="0.01"
              bind={form.ssa_discount_percentage}
            />
          </div>
        )}
      </div>

      <div class="space-y-4">
        <div class="flex items-center space-x-3">
          <Checkbox
            id="clearvue_has_agreement"
            checked={form.clearvue_has_agreement.value}
            onCheckedChange={checked => {
              form.set_form_data({ clearvue_has_agreement: checked === true })
            }}
          />
          <Label htmlFor="clearvue_has_agreement" class="text-sm font-medium text-gray-700">
            Has Clearvue agreement
          </Label>
        </div>
        {form.clearvue_has_agreement.value && (
          <div class="ml-7 space-y-4">
            <Input
              label="Clearvue Signed Date"
              type="date"
              name="clearvue_signed_date"
              bind={form.clearvue_signed_date}
            />
            <div class="flex items-center space-x-3">
              <Checkbox
                id="clearvue_proactive_discount"
                checked={form.clearvue_proactive_discount.value}
                onCheckedChange={checked => {
                  form.set_form_data({ clearvue_proactive_discount: checked === true })
                }}
              />
              <Label htmlFor="clearvue_proactive_discount" class="text-sm font-medium text-gray-700">
                Proactively give Clearvue discount on invoice
              </Label>
            </div>
            {form.clearvue_proactive_discount.value && (
              <div class="ml-7">
                <Input
                  label="Clearvue Invoice Start Date"
                  type="date"
                  name="clearvue_invoice_start_date"
                  bind={form.clearvue_invoice_start_date}
                />
              </div>
            )}
            <Input
              label="Clearvue Discount Percentage"
              type="number"
              name="clearvue_discount_percentage"
              placeholder="0.00"
              step="0.01"
              bind={form.clearvue_discount_percentage}
            />
          </div>
        )}
      </div>

      <div class="space-y-2">
        <Label htmlFor="delivery_method">Delivery Method</Label>
        <Select
          value={form.delivery_method.value}
          onValueChange={value => {
            form.set_form_data({ delivery_method: value })
          }}>
          <SelectTrigger id="delivery_method">
            <SelectValue placeholder="Select delivery method..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="FOB">Customer Pickup</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button type="submit" disabled={loading}>
        Save Configuration
      </Button>
    </Form>
  )
}

export function ConfigUnfiPage() {
  const [, setLocation] = useLocation()
  const [integration, setIntegration] = useState<Integration | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [loading, setLoading] = useState(true)

  useAsyncEffect(async () => {
    const res = await getIntegrations()
    if (res.ok) {
      const allIntegrations = res.value.data
      const emails = allIntegrations.filter((i: IntegrationWithJobInfo) => ["gmail", "microsoft"].includes(i.name))
      setEmailAccounts(emails)

      const unfiIntegration = allIntegrations.find((i: IntegrationWithJobInfo) => i.name === "unfi")
      if (unfiIntegration) {
        setIntegration(unfiIntegration)
      }
    }
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <CenterColumnContainer>
        <ConnectLogo src={unfiLogo} alt="UNFI" />
        <div class="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </CenterColumnContainer>
    )
  }

  if (!integration) {
    return (
      <CenterColumnContainer>
        <ConnectLogo src={unfiLogo} alt="UNFI" />
        <div class="text-center py-8">
          <p class="text-gray-500 mb-4">No UNFI integration found. Please connect UNFI first.</p>
          <Button
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            onClick={() => setLocation("/setup/connect/unfi")}>
            Connect UNFI
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer>
      <ConnectLogo src={unfiLogo} alt="UNFI" />
      <UnfiConfigForm
        integration={integration}
        emailAccounts={emailAccounts}
        onSuccess={() => setLocation("/setup/connect")}
      />
    </CenterColumnContainer>
  )
}
